import { useMemo, useState } from "react";
import { Column, Id, Task } from "./types";
import ColumnContainer from "./ColumnContainer";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { createPortal } from "react-dom";
import OrderCard from "./OrderCard";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/material";

const defaultCols = [
  {
    id: "PLACED",
    title: "PLACED",
  },
  {
    id: "CONFIRMED",
    title: "CONFIRMED",
  },
  {
    id: "ASSIGNED",
    title: "ASSIGNED",
  },
  {
    id: "IN_PROGRESS",
    title: "IN PROGRESS",
  },
  {
    id: "UNDER_REVISION",
    title: "UNDER REVISION",
  },
  {
    id: "INVOICED",
    title: "INVOICED",
  },
  {
    id: "SHIPPED",
    title: "SHIPPED",
  },
  {
    id: "COMPLETED",
    title: "COMPLETED",
  },
];

const defaultTasks = [
  {
    id: 1,
    columnId: "PLACED",
    orderNo: "BQ554901",
    commissionName: "BQ554901-EK-NR3-368-HAY...",
    product: "EK",
    category: "MOU",
    assigned: "Designer1",
    created: "15-08-2023 03:19 PM",
    processed: "NEW",
  },
  {
    id: 2,
    columnId: "IN_PROGRESS",
    orderNo: "BQ554901",
    commissionName: "BQ554901-EK-NR3-368-HAY...",
    product: "EK",
    category: "MOU",
    assigned: "Designer1",
    created: "15-08-2023 03:19 PM",
    processed: "NEW",
  },
  {
    id: 3,
    columnId: "UNDER_REVISION",
    orderNo: "BQ554901",
    commissionName: "BQ554901-EK-NR3-368-HAY...",
    product: "EK",
    category: "MOU",
    assigned: "Designer1",
    created: "15-08-2023 03:19 PM",
    processed: "NEW",
  },
  {
    id: 4,
    columnId: "PLACED",
    orderNo: "BQ554901",
    commissionName: "BQ554901-EK-NR3-368-HAY...",
    product: "EK",
    category: "MOU",
    assigned: "Designer1",
    created: "15-08-2023 03:19 PM",
    processed: "NEW",
  },
  {
    id: 5,
    columnId: "IN_PROGRESS",
    orderNo: "BQ554901",
    commissionName: "BQ554901-EK-NR3-368-HAY...",
    product: "EK",
    category: "MOU",
    assigned: "Designer1",
    created: "15-08-2023 03:19 PM",
    processed: "NEW",
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: "auto",
    height: "100%",

    "& .section": {
      display: "flex",
      flexFlow: "row",
      justifyContent: "flex-start",
      alignItems: "stretch",
      userSelect: "none",
      minWidth: "fit-content",
      height: "100%",
      gap: "0px",
    },
    "& .wrapper": {
      display: "flex",
      gap: "0px",

      "& .box": {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignSelf: "auto",
        flex: "1 1 auto",
        minWidth: "282px",
        maxWidth: "282px",

        "& .box-wrap": {
          borderRadius: "6px",
          display: "flex",
          flexDirection: "column",
          alignSelf: "auto",
          flex: "1 1 auto",
          marginReft: "8px",
          marginRight: "8px",
          backgroundColor: "#2A2E32",
        },
      },
    },
    "& .c-header": {
      "& small": {
        backgroundColor: "#47525D",
        borderRadius: "8px",
        fontSize: "10px",
        padding: "3px 8px",
        marginLeft: "5px",
      },
    },
    "& .cat-label": {
      backgroundColor: "#FFB101",
      borderRadius: "8px",
      fontSize: "10px",
      padding: "3px 8px",
    },
  },
}));

function Board(props) {
  const classes = useStyles(props);
  const [columns, setColumns] = useState(defaultCols);
  const columnsId = useMemo(() => columns.map((col) => col.id), [columns]);

  const [tasks, setTasks] = useState(defaultTasks);

  const [activeColumn, setActiveColumn] = useState(null);

  const [activeTask, setActiveTask] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  return (
    <Box className={clsx(classes.root)}>
      <Box className="section">
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
      >
          <div className="wrapper">
            <SortableContext items={columnsId}>
              {columns.map((col) => (
                <ColumnContainer
                  key={col.id}
                  column={col}
                  tasks={tasks.filter((task) => task.columnId === col.id)}
                />
              ))}
            </SortableContext>
          </div>

        {createPortal(
          <DragOverlay>
            {activeColumn && (
              <ColumnContainer
                column={activeColumn}
                tasks={tasks.filter(
                  (task) => task.columnId === activeColumn.id
                )}
              />
            )}
            {activeTask && <OrderCard task={activeTask} />}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
      </Box>
    </Box>
  );

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === "Column") {
      setActiveColumn(event.active.data.current.column);
      return;
    }

    if (event.active.data.current?.type === "Task") {
      setActiveTask(event.active.data.current.task);
      return;
    }
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveColumn(null);
    setActiveTask(null);

    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveAColumn = active.data.current?.type === "Column";
    if (!isActiveAColumn) return;

    console.log("DRAG END");

    setColumns((columns) => {
      const activeColumnIndex = columns.findIndex((col) => col.id === activeId);

      const overColumnIndex = columns.findIndex((col) => col.id === overId);

      return arrayMove(columns, activeColumnIndex, overColumnIndex);
    });
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveATask = active.data.current?.type === "Task";
    const isOverATask = over.data.current?.type === "Task";

    if (!isActiveATask) return;

    // Im dropping a Task over another Task
    if (isActiveATask && isOverATask) {
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.id === activeId);
        const overIndex = tasks.findIndex((t) => t.id === overId);

        if (tasks[activeIndex].columnId != tasks[overIndex].columnId) {
          // Fix introduced after video recording
          tasks[activeIndex].columnId = tasks[overIndex].columnId;
          return arrayMove(tasks, activeIndex, overIndex - 1);
        }

        return arrayMove(tasks, activeIndex, overIndex);
      });
    }

    const isOverAColumn = over.data.current?.type === "Column";

    // Im dropping a Task over a column
    if (isActiveATask && isOverAColumn) {
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.id === activeId);

        tasks[activeIndex].columnId = overId;
        console.log("DROPPING TASK OVER COLUMN", { activeIndex });
        return arrayMove(tasks, activeIndex, activeIndex);
      });
    }
  }
}
export default Board;
